import React from "react"
import { Tag } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import {
  ProposalStatus,
  StoredProposal,
  useAssistantStore,
} from "../assistantStore"
import { useAssistantScreen } from "../screenRegistry"
import type { Proposal, ProposalKind } from "../types"
import ScriptProposalCard from "./ScriptProposalCard.component"

const KIND_LABEL: Record<ProposalKind, string> = {
  "qnr-edit": "Questionnaire edit",
  "template-fill": "Template",
  tabspec: "Tables",
  dashboard: "Dashboard",
  script: "Script",
}

/** The screen a proposal of each kind is reviewed on. */
const REVIEW_SCREEN: Record<ProposalKind, string> = {
  "qnr-edit": "questionnaire editor",
  "template-fill": "questionnaire editor",
  tabspec: "tab spec",
  dashboard: "dashboard",
  script: "Query Console",
}

const STATUS_LABEL: Record<Exclude<ProposalStatus, "pending">, string> = {
  accepted: "Accepted",
  rejected: "Rejected",
  stale: "Out of date",
}

export const STALE_TEXT =
  "This no longer matches what is open. Ask again to get a new proposal."
/** The card's word for a proposal whose check could not run (DATA(Q) busy): not known to fail. */
export const UNCHECKED_TEXT = "Couldn't be checked"

/** How many check errors a card lists before "and n more". */
const MAX_ERRORS = 5

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`

/**
 * How a check error names its line. A questionnaire edit is checked as the
 * whole questionnaire with the edits made, so its line numbers are those of
 * the text after the change, not the editor's lines now.
 */
export const errorLinePrefix = (p: Proposal, line: number | undefined): string => {
  if (line === undefined) return ""
  return p.kind === "qnr-edit" ? `Line ${line} after the change: ` : `Line ${line}: `
}

/**
 * What a Tables or Dashboard proposal drops from the open spec or dashboard
 * ("Removes 2 tables", "Removes 1 tile"), or null.
 */
export const removalWarning = (p: Proposal): string | null => {
  const [removed, what] =
    p.kind === "tabspec"
      ? [p.removedTableIds, "table"]
      : p.kind === "dashboard"
        ? [p.removedTileIds, "tile"]
        : [undefined, ""]
  if (!Array.isArray(removed)) return null
  return removed.length > 0 ? `Removes ${plural(removed.length, what)}` : null
}

/** The card's one-line summary of what the proposal changes. */
export const proposalSummary = (p: Proposal): string => {
  switch (p.kind) {
    case "qnr-edit": {
      const where = p.variant ? ` (variant ${p.variant})` : ""
      return `${plural(p.edits.length, "change")} to the questionnaire${where}`
    }
    case "template-fill":
      return `The template "${p.block.title}", filled in`
    case "tabspec":
      return `${plural(p.changedTableIds.length, "table")} added or changed in "${p.name}"`
    case "dashboard":
      return `${plural(p.changedTileIds.length, "tile")} added or changed in "${p.name}"`
    case "script":
      return `The script "${p.name}"`
  }
}

/**
 * One proposal in the panel: its kind, what it changes, whether it passed
 * its check ("Not working" with the errors when it did not, "Couldn't be
 * checked" with the reason when the check could not run), and Review /
 * Reject while it waits for the user. Review hands it to the open screen's
 * handler for its kind; a screen without one says where to review it.
 * A script is the exception, the same on every screen: the card shows it
 * with its own name field, Save and Open in Console (ScriptProposalCard).
 */
const ProposalCard: React.FC<{ stored: StoredProposal }> = ({ stored }) => {
  const { projectId, registration } = useAssistantScreen()
  const setStatus = useAssistantStore((s) => s.setProposalStatus)
  const { proposal: p, status } = stored
  // A script is saved from the card itself, so no screen handles it.
  const handler = p.kind === "script" ? undefined : registration?.handlers[p.kind]
  const stale = status === "stale" || (status === "pending" && !!handler?.isStale?.(p))
  const shownStatus: ProposalStatus = stale ? "stale" : status
  const blocked = handler?.blockedReason?.(p) ?? null
  const undoable = status === "accepted" && !!handler?.canUndo?.(p)
  const removal = removalWarning(p)
  const { validation } = p

  const review = (): void => {
    if (!handler) return
    if (handler.isStale?.(p)) {
      setStatus(projectId, p.id, "stale")
      return
    }
    handler.review(p)
  }

  return (
    <div className="rounded border border-gray-200 bg-white p-2 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Tag className="m-0">{KIND_LABEL[p.kind]}</Tag>
        {shownStatus !== "pending" && (
          <Tag className="m-0" color={shownStatus === "accepted" ? "green" : undefined}>
            {STATUS_LABEL[shownStatus]}
          </Tag>
        )}
      </div>
      <div className="text-sm">{proposalSummary(p)}</div>
      {removal && <div className="text-xs text-amber-700">{removal}</div>}
      {validation.ok ? (
        <div className="text-xs">
          <div className="text-green-700">Checked</div>
          {/* Notes on a passing check, e.g. how many tables or tiles it did not run */}
          {validation.errors.length > 0 && (
            <ul className="m-0 pl-4 list-disc text-gray-500">
              {validation.errors.slice(0, MAX_ERRORS).map((e, i) => (
                <li key={i}>{e.message}</li>
              ))}
            </ul>
          )}
        </div>
      ) : validation.unchecked ? (
        <div className="text-xs text-amber-700">
          <div className="font-semibold">{UNCHECKED_TEXT}</div>
          {validation.errors.length > 0 && (
            <ul className="m-0 pl-4 list-disc">
              {validation.errors.slice(0, MAX_ERRORS).map((e, i) => (
                <li key={i}>{e.message}</li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="text-xs text-red-600">
          <div className="font-semibold">Not working</div>
          {validation.errors.length > 0 && (
            <ul className="m-0 pl-4 list-disc">
              {validation.errors.slice(0, MAX_ERRORS).map((e, i) => (
                <li key={i}>
                  {errorLinePrefix(p, e.line)}
                  {e.message}
                </li>
              ))}
              {validation.errors.length > MAX_ERRORS && (
                <li>and {validation.errors.length - MAX_ERRORS} more</li>
              )}
            </ul>
          )}
        </div>
      )}
      {shownStatus === "stale" && (
        <div className="text-xs text-gray-500">{STALE_TEXT}</div>
      )}
      {p.kind === "script" && (
        <ScriptProposalCard proposal={p} status={shownStatus} savedAs={stored.savedAs} />
      )}
      {shownStatus === "pending" && p.kind !== "script" && (
        <div className="flex items-center gap-2 pt-1">
          {handler ? (
            <DataQButton
              type="primary"
              size="small"
              isDisabled={blocked !== null}
              onClick={review}
            >
              Review
            </DataQButton>
          ) : (
            <span className="text-xs text-gray-500">
              Open the {REVIEW_SCREEN[p.kind]} to review this
            </span>
          )}
          <DataQButton
            type="secondary"
            size="small"
            onClick={() => setStatus(projectId, p.id, "rejected")}
          >
            Reject
          </DataQButton>
        </div>
      )}
      {shownStatus === "pending" && blocked && (
        <div className="text-xs text-gray-500">{blocked}</div>
      )}
      {undoable && (
        <div className="flex items-center gap-2 pt-1">
          <DataQButton type="secondary" size="small" onClick={() => handler?.undo?.(p)}>
            Undo
          </DataQButton>
        </div>
      )}
    </div>
  )
}

export default ProposalCard
